/**
 * The home's plan section, as answers (owner rulings 2026-10-16).
 *
 * Pure functions over rows useHomePlan reads. Three situations:
 *
 *   none      no plan in force, and none being made
 *   making    no plan in force; a cycle is making one
 *   in_force  a plan in force; the next one may be being made
 *
 * "The plan" is the Plan of Record in force (plan_force_states, migration 919;
 * its ranks in item_official_ranks), not the cycle report the old home read.
 * A prioritization cycle is "being made" while it is a draft or active cycle
 * that no authorized plan came from and, when a plan is in force, that was
 * started after that plan took effect. Only people in a cycle's flow see it.
 */

import type { PlanForceRow } from '../../../lib/planForceService';

const DAY_MS = 86_400_000;

export function shortDate(iso: string | Date | null | undefined): string | null {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

// ── Which plan ─────────────────────────────────────────────────────────────

/** The plan in force for the whole organization, else the first in force for any part of it. */
export function pickPlanInForce(states: PlanForceRow[]): PlanForceRow | null {
  const inForce = states.filter((s) => s.state === 'in_force');
  return inForce.find((s) => s.scopeOrgUnitId === null) ?? inForce[0] ?? null;
}

/** An authorized plan waiting for its start date: the in-force plan holds until then. */
export function pickStartsLater(states: PlanForceRow[]): PlanForceRow | null {
  return (
    states
      .filter((s) => s.state === 'starts_later' && s.takesEffectOn)
      .sort((a, b) => (a.takesEffectOn ?? '').localeCompare(b.takesEffectOn ?? ''))[0] ?? null
  );
}

// ── Mine ───────────────────────────────────────────────────────────────────

export interface ItemPeople {
  ownerId: string | null;
  delegateId: string | null;
}

/** An item is yours when you own it or it has been handed to you to do (ownership_mode 'delegated'). */
export function isMine(people: ItemPeople | undefined, viewerId: string | null | undefined): boolean {
  if (!people || !viewerId) return false;
  return String(people.ownerId ?? '') === viewerId || String(people.delegateId ?? '') === viewerId;
}

// ── The plan's list ────────────────────────────────────────────────────────

export interface PlanRank {
  itemId: string;
  title: string;
  rank: number;
  aboveTheLine: boolean;
}

export function splitAtLine(ranks: PlanRank[]): { doing: PlanRank[]; notDoing: PlanRank[] } {
  const sorted = [...ranks].sort((a, b) => a.rank - b.rank);
  return { doing: sorted.filter((r) => r.aboveTheLine), notDoing: sorted.filter((r) => !r.aboveTheLine) };
}

// ── Since it was agreed ────────────────────────────────────────────────────

/**
 * The item events that count as a change to the item — the same list the
 * database uses to tell an item's watchers (trg_notify_watched_item_changed,
 * migration 864).
 */
export const ITEM_CHANGE_EVENTS = [
  'item_title_updated',
  'item_description_updated',
  'item_category_updated',
  'item_altitude_updated',
  'altitude_set',
  'item_transition',
  'item_marked_complete',
  'item_promoted',
  'ITEM_PROMOTED',
  'item_parent_updated',
  'item_split',
  'relationship_edge_added',
  'relationship_edge_dissolved',
  'VALUE_ACCEPTED',
  'value_acceptance',
] as const;

export interface ItemChangeEvent {
  itemId: string;
  createdBy: string | null;
  createdAt: string;
}

export interface SinceAgreed {
  /** Items with at least one change since the plan was authorized. */
  items: number;
  doing: number;
  notDoing: number;
  /** Whoever made the most of the changes, and the latest change's time. */
  mostlyBy: string | null;
  lastAt: string | null;
}

export function sinceAgreed(events: ItemChangeEvent[], doingIds: ReadonlySet<string>): SinceAgreed {
  const items = new Set(events.map((e) => e.itemId));
  const byPerson = new Map<string, number>();
  let lastAt: string | null = null;
  for (const e of events) {
    if (e.createdBy) byPerson.set(e.createdBy, (byPerson.get(e.createdBy) ?? 0) + 1);
    if (!lastAt || e.createdAt > lastAt) lastAt = e.createdAt;
  }
  const mostlyBy = [...byPerson.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
  const doing = [...items].filter((id) => doingIds.has(id)).length;
  return { items: items.size, doing, notDoing: items.size - doing, mostlyBy, lastAt };
}

// ── Who agreed ─────────────────────────────────────────────────────────────

export interface RankingSessionFact {
  name: string;
  publishedAt: string | null;
  facilitatorName: string | null;
  participantCount: number;
}

export interface LoopTurnFact {
  submittedAt: string | null;
  submittedByName: string | null;
  resolvedAt: string | null;
  resolvedByName: string | null;
  /** loop_results.evaluator_decision: returned, accepted, accepted_provisional, rejected… */
  decision: string | null;
}

export interface AuthorizationFact {
  at: string | null;
  version: number | null;
  byLabel: string | null;
}

export interface TrailLine {
  when: string | null;
  text: string;
}

const DECISION_VERB: Record<string, string> = {
  returned: 'sent it back',
  revision_requested: 'sent it back',
  accepted: 'accepted it',
  accepted_provisional: 'accepted it provisionally',
  rejected: 'turned it down',
};

/** The path from ranking to authorization, one dated sentence per act. */
export function whoAgreedTrail(input: {
  session: RankingSessionFact | null;
  turns: LoopTurnFact[];
  authorization: AuthorizationFact | null;
}): TrailLine[] {
  const lines: TrailLine[] = [];
  const { session, turns, authorization } = input;
  if (session) {
    const people = session.participantCount > 0 ? `${plural(session.participantCount, 'person', 'people')} ranked it` : 'It was ranked';
    lines.push({
      when: shortDate(session.publishedAt),
      text: `${people} in “${session.name}”${session.facilitatorName ? `, run by ${session.facilitatorName}` : ''}.`,
    });
  }
  turns.forEach((t, i) => {
    const sender = t.submittedByName ?? 'Someone';
    let text = `${sender} sent it up${i > 0 ? ' again' : ''}.`;
    const verb = t.decision ? DECISION_VERB[t.decision] : undefined;
    if (t.resolvedAt && verb) text += ` ${t.resolvedByName ?? 'The receiver'} ${verb}.`;
    else if (!t.resolvedAt) text += ' No answer yet.';
    lines.push({ when: shortDate(t.submittedAt), text });
  });
  if (authorization?.at) {
    const version = authorization.version != null ? `, version ${authorization.version}` : '';
    lines.push({
      when: shortDate(authorization.at),
      text: `Authorized as the plan of record${version}${authorization.byLabel ? `, by ${authorization.byLabel}` : ''}.`,
    });
  }
  return lines;
}

// ── A cycle being made ─────────────────────────────────────────────────────

export interface CycleLite {
  id: string;
  name: string;
  status: string;
  ownerId: string | null;
  createdBy: string | null;
  createdAt: string | Date | null;
  updatedAt: string | Date | null;
}

const MAKING_STATUSES = new Set(['draft', 'active']);

function time(v: string | Date | null | undefined): number {
  if (!v) return 0;
  const t = (v instanceof Date ? v : new Date(v)).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Cycles that are making a plan: drafts and active cycles that no authorized
 * plan came from. When a plan is in force, only cycles started after it took
 * effect count — an older open cycle is the one that made it, or left over.
 * Active before draft, newest first.
 */
export function cyclesBeingMade(
  cycles: CycleLite[],
  sourceCycleIds: ReadonlySet<string>,
  planInForceSince: string | null,
): CycleLite[] {
  const since = planInForceSince ? time(planInForceSince) : null;
  return cycles
    .filter((c) => MAKING_STATUSES.has(c.status) && !sourceCycleIds.has(c.id))
    .filter((c) => since === null || time(c.createdAt) > since)
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
      return time(b.updatedAt) - time(a.updatedAt);
    });
}

export interface FlowFacts {
  viewerId: string;
  isAdmin: boolean;
  ownerId: string | null;
  createdBy: string | null;
  /** Nothing designed yet: no tracks, no participants, no items. */
  designed: boolean;
  /** The viewer sits on one of the cycle's tracks (track_participants). */
  onATrack: boolean;
  /** Org units the cycle's tracks are scoped to. */
  trackUnitIds: string[];
  /** For each of the viewer's org placements: the unit and every unit above it (org_units.path). */
  viewerUnitPaths: string[][];
}

/**
 * Whether the viewer sees a cycle being made (owner ruling 2026-10-16: only
 * people whose part of the organization is in the cycle's flow). A cycle not
 * yet designed is seen only by whoever started or owns it, and admins.
 * A display rule: the database still lets every member read the cycle.
 */
export function seesCycleBeingMade(f: FlowFacts): boolean {
  if (f.isAdmin) return true;
  if (f.ownerId === f.viewerId || f.createdBy === f.viewerId) return true;
  if (!f.designed) return false;
  if (f.onATrack) return true;
  return f.trackUnitIds.some((unit) => f.viewerUnitPaths.some((path) => path.includes(unit)));
}

// ── The cycle's steps ──────────────────────────────────────────────────────

export type StepState = 'done' | 'now' | 'todo';

export interface CycleStep {
  key: string;
  label: string;
  state: StepState;
  note: string | null;
}

export interface StepFacts {
  participants: number;
  items: number;
  criteriaLocked: boolean;
  valuesDone: boolean;
  rankingDone: boolean;
  /** The cycle passes its ranking up and back (it has a catchball loop or a designed path). */
  hasCatchball: boolean;
  agreed: 'no' | 'provisional' | 'yes';
  authorized: boolean;
}

/**
 * The steps this cycle goes through, and where it is. The five stages, then
 * agreement up the line when the cycle has one, then authorization. A later
 * step done marks every earlier one done: a stage can be settled outside the
 * cycle's own gates (an imported ranking, a session run elsewhere).
 */
export function cycleSteps(f: StepFacts): CycleStep[] {
  const raw: Array<{ key: string; label: string; done: boolean; note: string | null }> = [
    { key: 'participants', label: 'Participants', done: f.participants > 0, note: f.participants > 0 ? plural(f.participants, 'person', 'people') : null },
    { key: 'items', label: 'Items', done: f.items > 0, note: f.items > 0 ? plural(f.items, 'item', 'items') : null },
    { key: 'criteria', label: 'Criteria', done: f.criteriaLocked, note: null },
    { key: 'values', label: 'Values', done: f.valuesDone, note: null },
    { key: 'ranking', label: 'Ranking', done: f.rankingDone, note: null },
  ];
  if (f.hasCatchball) {
    raw.push({ key: 'agreed', label: 'Agreed', done: f.agreed === 'yes', note: f.agreed === 'provisional' ? 'provisionally' : null });
  }
  raw.push({ key: 'authorized', label: 'Authorized', done: f.authorized, note: null });

  let lastDone = -1;
  raw.forEach((s, i) => {
    if (s.done) lastDone = i;
  });
  return raw.map((s, i) => ({
    key: s.key,
    label: s.label,
    note: i <= lastDone ? s.note : i === lastDone + 1 ? s.note ?? 'now' : null,
    state: i <= lastDone ? 'done' : i === lastDone + 1 ? 'now' : 'todo',
  }));
}

// ── What would change ──────────────────────────────────────────────────────

export interface ProposedRank {
  itemId: string;
  title: string;
}

export interface ChangeRow {
  itemId: string;
  title: string;
  /** Its rank in the proposed list, when it has one. */
  newRank: number | null;
  /** Its rank in the plan in force, when it has one. */
  wasRank: number | null;
}

export interface WhatWouldChange {
  doneAndLeaving: ChangeRow[];
  newAboveTheLine: ChangeRow[];
  droppingBelow: ChangeRow[];
  staying: number;
}

/**
 * The proposed ranking against the plan in force. Null when the proposal draws
 * no line yet — without one, "above" and "below" mean nothing. An item
 * delivered (item_deliveries 'shipped') and missing from the proposal's top is
 * leaving because it is done, not dropping.
 */
export function whatWouldChange(
  plan: PlanRank[],
  proposed: ProposedRank[],
  proposedCut: number | null,
  delivered: ReadonlySet<string>,
): WhatWouldChange | null {
  if (proposedCut == null || proposed.length === 0) return null;
  const planAbove = plan.filter((r) => r.aboveTheLine).sort((a, b) => a.rank - b.rank);
  const planAboveIds = new Set(planAbove.map((r) => r.itemId));
  const wasRank = new Map(plan.map((r) => [r.itemId, r.rank]));
  const newRank = new Map(proposed.map((p, i) => [p.itemId, i + 1]));
  const proposedAbove = proposed.slice(0, proposedCut);
  const proposedAboveIds = new Set(proposedAbove.map((p) => p.itemId));

  const leaving = planAbove.filter((r) => !proposedAboveIds.has(r.itemId));
  return {
    doneAndLeaving: leaving
      .filter((r) => delivered.has(r.itemId))
      .map((r) => ({ itemId: r.itemId, title: r.title, newRank: newRank.get(r.itemId) ?? null, wasRank: r.rank })),
    newAboveTheLine: proposedAbove
      .filter((p) => !planAboveIds.has(p.itemId))
      .map((p) => ({ itemId: p.itemId, title: p.title, newRank: newRank.get(p.itemId) ?? null, wasRank: wasRank.get(p.itemId) ?? null })),
    droppingBelow: leaving
      .filter((r) => !delivered.has(r.itemId))
      .map((r) => ({ itemId: r.itemId, title: r.title, newRank: newRank.get(r.itemId) ?? null, wasRank: r.rank })),
    staying: planAbove.filter((r) => proposedAboveIds.has(r.itemId)).length,
  };
}

// ── Needs fixing ───────────────────────────────────────────────────────────

export interface ProvisionalAgreement {
  loopId: string;
  cycleId: string;
  cycleName: string;
  ownerId: string | null;
  ballHolderId: string | null;
  acceptedAt: string;
}

export interface FixRow {
  id: string;
  text: string;
  who: string;
  href: string;
}

export const PROVISIONAL_GRACE_DAYS = 7;

/**
 * An agreement accepted provisionally and never confirmed, a week on. Shown
 * only to the people who can settle it: the cycle's owner, whoever holds the
 * ball, and admins.
 */
export function needsFixing(
  rows: ProvisionalAgreement[],
  viewer: { id: string; isAdmin: boolean },
  names: Record<string, string>,
  today: Date = new Date(),
): FixRow[] {
  return rows
    .filter((r) => today.getTime() - time(r.acceptedAt) >= PROVISIONAL_GRACE_DAYS * DAY_MS)
    .filter((r) => viewer.isAdmin || r.ownerId === viewer.id || r.ballHolderId === viewer.id)
    .map((r) => {
      const owner = r.ownerId ? names[r.ownerId] ?? null : null;
      return {
        id: r.loopId,
        text: `The ranking from ${r.cycleName} was accepted provisionally on ${shortDate(r.acceptedAt)} and never confirmed.`,
        who:
          r.ownerId === viewer.id
            ? 'You own the cycle and can confirm it.'
            : owner
              ? `${owner} owns the cycle and can confirm it.`
              : 'The cycle owner can confirm it.',
        href: `/prioritization/cycles/${r.cycleId}`,
      };
    });
}

// ── The line under the greeting ────────────────────────────────────────────

export interface SummaryFacts {
  /** `mine`: how many of the items we are doing are the viewer's. */
  plan: { name: string; doing: number; notDoing: number; mine: number } | null;
  making: { name: string } | null;
  /** The plan section could not be read: say nothing about the plan. */
  unread?: boolean;
}

/**
 * The plan half of the line under the greeting: "what are we doing?" The other
 * half, what needs the person, names each thing and is drawn by the page
 * (sections.needsYouLine), since a count alone gives nobody anything to act on.
 */
export function summaryLine(f: SummaryFacts): string {
  if (f.unread) return '';
  if (f.plan) {
    const name = /^the /i.test(f.plan.name) ? f.plan.name : `The ${f.plan.name}`;
    let s = `${name} stands: ${plural(f.plan.doing, 'thing', 'things')} we are doing, ${f.plan.notDoing} we are not.`;
    if (f.plan.mine > 0) s += ` ${f.plan.mine} of the ${f.plan.doing} ${f.plan.mine === 1 ? 'is' : 'are'} yours.`;
    if (f.making) s += ` The next plan is being made.`;
    return s;
  }
  if (f.making) return `There is no plan in force; ${f.making.name} is making one.`;
  return `There is no plan right now, and none is being made.`;
}
